import { useState, useMemo } from 'react'
import { useMutation, useQuery } from 'urql'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import Button from 'app/components/Button'
import FormInput from 'app/components/form/FormInput'
import FormCombo from 'app/components/form/FormCombo'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { SEARCH_AVAILABLE_QUERY, PURCHASE_MUTATION } from 'app/components/location-phone-numbers/graphql'
import { PhoneNumberType } from 'app/generated/graphql'
import type { OptionType } from 'types/OptionType'

type IvrProvisioningModalProps = {
  locationId: string
  onSuccess: (entry: { id: string; phone: string; label: string }) => void
}

export function IvrProvisioningModal({ locationId, onSuccess }: IvrProvisioningModalProps) {
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])

  const [label, setLabel] = useState(``)
  const [areaCode, setAreaCode] = useState(``)
  const [selectedPhone, setSelectedPhone] = useState<OptionType | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirm] = useConfirmation()

  const shouldSearch = areaCode.length === 3

  const [searchResult] = useQuery({
    query: SEARCH_AVAILABLE_QUERY,
    variables: { type: PhoneNumberType.Voice, areaCode, limit: 5 },
    pause: !shouldSearch,
    context: opsContext,
  })

  const [, executePurchase] = useMutation(PURCHASE_MUTATION)

  const availableNumbers = searchResult.data?.searchAvailablePhoneNumbers ?? []

  const phoneOptions: OptionType[] = availableNumbers.map((num) => ({
    id: num.phone,
    name: num.locality ? `${num.phone} — ${num.locality}` : num.phone,
    value: num.phone,
  }))

  const handleSubmit = () => {
    if (!label || !selectedPhone) {
      notifyError(`Select a number and enter a label.`)
      return
    }
    confirm({
      variant: `info`,
      title: `Purchase IVR Number`,
      description: `This will purchase ${selectedPhone.name} as an IVR number. Continue?`,
    }).then(async () => {
      setIsSubmitting(true)
      const { data, error } = await executePurchase(
        {
          input: {
            locationId,
            phone: selectedPhone.value,
            type: PhoneNumberType.Voice,
            roles: ['ivr'],
            label,
          },
        },
        opsContext,
      )

      if (error) {
        logError('IVR provisioning failed', error)
        notifyError(error.message)
        setIsSubmitting(false)
        return
      }

      const record = data?.purchasePhoneNumber
      if (record) {
        notifySuccess(`IVR number provisioned`)
        onSuccess({ id: record.id, phone: record.phone, label })
        closeModal()
      }
      setIsSubmitting(false)
    })
  }

  return (
    <div className="w-[400px] px-6 py-4">
      <div className="flex flex-col gap-y-4">
        <FormInput
          cols={6}
          name="ivrLabel"
          label="Label"
          labelClassName="text-body-sm text-strong"
          placeholder="e.g. Main IVR Line"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <FormInput
          cols={6}
          name="ivrAreaCode"
          label="Area Code"
          labelClassName="text-body-sm text-strong"
          placeholder="e.g. 916"
          value={areaCode}
          maxLength={3}
          onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ``).slice(0, 3))}
        />

        {shouldSearch && searchResult.fetching && (
          <p className="text-body-sm text-minimal">Searching available numbers...</p>
        )}

        {shouldSearch && !searchResult.fetching && searchResult.error && (
          <div className="mt-[9px] text-body-sm text-red-600">Search failed: {searchResult.error.message}</div>
        )}

        {shouldSearch && !searchResult.fetching && !searchResult.error && phoneOptions.length > 0 && (
          <FormCombo
            cols={6}
            name="ivrPhone"
            label="Available Numbers"
            labelClassName="text-body-sm text-strong"
            inputClassName="!h-10 !px-2 !py-1 text-body-sm"
            placeholder="Select a number"
            value={selectedPhone}
            options={phoneOptions}
            onChange={(option: OptionType) => setSelectedPhone(option)}
          />
        )}

        {shouldSearch && !searchResult.fetching && !searchResult.error && phoneOptions.length === 0 && (
          <div className="mt-[9px] text-body-sm text-red-600">No numbers available in area code {areaCode}.</div>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} disabled={isSubmitting} />
        <Button
          label="Purchase"
          mode="primary"
          size="md"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || !label || !selectedPhone}
        />
      </div>
    </div>
  )
}
